import { Link, useParams } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardContent } from "@/components/ui";
import {
  formatPriceCents,
  getBrandBySlug,
  getModelsForBrand,
} from "@/data/brands";
import { useSeo } from "@/lib/seo";

export function BrandPage() {
  const { brand: brandSlug } = useParams<{ brand: string }>();
  const brand = brandSlug ? getBrandBySlug(brandSlug) : undefined;
  const allModels = brandSlug ? getModelsForBrand(brandSlug) : [];
  const [modelSearch, setModelSearch] = useState("");
  const aboutCardRef = useRef<HTMLElement | null>(null);
  const [aboutParallaxY, setAboutParallaxY] = useState(0);

  const filteredModels = useMemo(() => {
    const q = modelSearch.trim().toLowerCase();
    if (!q) return allModels;
    return allModels.filter(
      (m) =>
        m.name.toLowerCase().includes(q) || m.slug.toLowerCase().includes(q),
    );
  }, [allModels, modelSearch]);

  useEffect(() => {
    let raf = 0;

    const update = () => {
      raf = 0;
      const el = aboutCardRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;

      // Subtle parallax: when the card is near center, move less; towards edges, move a bit more.
      const center = rect.top + rect.height / 2;
      const distanceFromCenter = center - vh / 2;
      const raw = -distanceFromCenter * 0.04; // tune strength here

      // Clamp so it stays subtle and doesn't reveal empty edges.
      const clamped = Math.max(-18, Math.min(18, raw));
      setAboutParallaxY(clamped);
    };

    const onScrollOrResize = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  if (!brand) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container-page py-16">
          <Card className="max-w-xl mx-auto text-center">
            <CardContent className="py-10">
              <h1 className="text-2xl font-bold text-gray-900">
                Brand not found
              </h1>
              <p className="mt-2 text-gray-600">
                We couldn’t find that brand. Try browsing all brands.
              </p>
              <Link to="/brands" className="inline-block mt-6">
                <Button>Browse Brands</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const heroImageUrl = `/brand-images/heroes/${brand.slug}.webp`;
  // OG/Twitter image: use JPG for best unfurl compatibility.
  const shareImageUrl = `/brand-images/${brand.slug}.jpg`;

  useSeo({
    title: brand.seo.title,
    description: brand.seo.description,
    image: shareImageUrl,
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero - destinations-style with brand image */}
      <header className="relative min-h-[70vh] flex items-start justify-center pt-28 md:pt-32 pb-16 overflow-hidden">
        {/* Background: brand image with gradient fallback */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-gray-900"
          style={{ backgroundImage: `url('${heroImageUrl}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60" />
        <div className="absolute inset-0 bg-black/35" />

        {/* Content */}
        <div className="relative z-10 w-full">
          <div className="text-center px-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6 text-sm">
              <Link
                to="/brands"
                className="text-white hover:text-white/90 transition-colors"
              >
                Brands
              </Link>
              <span className="text-white">/</span>
              <span className="text-white font-medium">{brand.name}</span>
            </div>
            <div className="mb-8">
              <h1 className="font-display text-5xl md:text-6xl text-white mb-4 leading-tight tracking-tight">
                {brand.name}
              </h1>
              <p className="text-white text-lg md:text-xl max-w-2xl mx-auto">
                Discover {allModels.length} models and the full {brand.name}{" "}
                lineup
              </p>
              <div className="w-24 h-px bg-white mx-auto mt-6" />
            </div>
          </div>

          {/* Search form - backdrop blur */}
          <div className="mt-8 w-full px-6">
            <form className="w-full" onSubmit={(e) => e.preventDefault()}>
              <div className="w-full max-w-2xl mx-auto">
                <div className="backdrop-blur-sm rounded-md shadow-lg w-full bg-black/20 border border-white/10">
                  <div className="p-6">
                    <label
                      htmlFor="brand-model-search"
                      className="block text-xs uppercase tracking-widest text-white mb-2 text-left font-medium"
                    >
                      Search {brand.name} models
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        id="brand-model-search"
                        type="text"
                        placeholder="Search by model name..."
                        value={modelSearch}
                        onChange={(e) => setModelSearch(e.target.value)}
                        className="flex-1 bg-transparent text-white placeholder-white/50 text-base outline-none"
                      />
                      <button
                        type="submit"
                        className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-md"
                      >
                        <Search className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </header>

      {/* About section - white page background, image inside glass card */}
      <section className="bg-white py-16 md:py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <section
            ref={aboutCardRef}
            className="relative rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
          >
            {/* Background image inside card */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat will-change-transform"
              style={{
                backgroundImage: `url('${heroImageUrl}')`,
                transform: `translate3d(0, ${aboutParallaxY}px, 0) scale(1.06)`,
              }}
              aria-hidden
            />
            <div className="absolute inset-0 bg-black/45" aria-hidden />
            <div className="absolute inset-0 backdrop-blur-[2px]" aria-hidden />

            {/* Foreground content */}
            <div
              className="relative z-10 p-6 md:p-10 lg:p-12 backdrop-blur-xl bg-black/40"
              style={{ backdropFilter: "blur(20px) saturate(180%)" }}
            >
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-white mb-5 md:mb-6 font-semibold">
                About {brand.name}
              </h2>
              <p className="drop-shadow-2xl font-normal text-white">
                {brand.editorial}
              </p>
            </div>
          </section>
        </div>
      </section>

      {/* Main content */}
      <div className="bg-gray-50">
        <div className="container-page py-8">
          <div className="mb-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <Link to={`/vehicles?make=${encodeURIComponent(brand.slug)}`}>
              <Button size="lg">
                Browse {brand.name} Inventory
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/apply">
              <Button size="lg" variant="outline">
                Apply for Financing
              </Button>
            </Link>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Popular {brand.name} models
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    This content is pulled directly from the client’s branch
                    copy under `/br/`.
                  </p>

                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {brand.popularModels.map((m) => (
                      <Link
                        key={m.slug}
                        to={`/brands/${brand.slug}/${m.slug}`}
                        className="rounded-lg border border-gray-200 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900">
                              {m.name}
                            </h3>
                            <p className="mt-1 text-sm text-gray-600">
                              Starting around{" "}
                              {formatPriceCents(m.startingPrice)}
                            </p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Buyer’s guide
                  </h2>
                  <div className="mt-4 space-y-4">
                    {brand.buyerGuide.map((section) => (
                      <div key={section.title}>
                        <p className="font-medium text-gray-900">
                          {section.title}
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                          {section.content}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900">
                    All {brand.name} models
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {filteredModels.length} model pages
                    {modelSearch.trim() ? ` matching "${modelSearch}"` : ""}.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {filteredModels.slice(0, 30).map((m) => (
                      <Link
                        key={m.slug}
                        to={`/brands/${brand.slug}/${m.slug}`}
                        className="badge bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-800"
                      >
                        {m.name}
                      </Link>
                    ))}
                    {filteredModels.length > 30 && (
                      <span className="badge bg-gray-100 text-gray-700">
                        +{filteredModels.length - 30} more
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Brand FAQs
                  </h2>
                  <div className="mt-4 space-y-3">
                    {brand.faqs.map((faq) => (
                      <details
                        key={faq.question}
                        className="group rounded-lg border border-gray-200 bg-white"
                      >
                        <summary className="cursor-pointer list-none p-4 flex items-start justify-between gap-4">
                          <span className="font-medium text-gray-900">
                            {faq.question}
                          </span>
                          <span className="mt-0.5 text-gray-400 group-open:rotate-180 transition-transform">
                            ▾
                          </span>
                        </summary>
                        <div className="px-4 pb-4 -mt-1">
                          <p className="text-sm text-gray-600">{faq.answer}</p>
                        </div>
                      </details>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary-50 border-primary-200">
                <CardContent>
                  <h3 className="font-semibold text-gray-900">Next step</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Pick a model to see model-specific text, then choose a year
                    to land on a dedicated year page.
                  </p>
                  <Link
                    to={`/brands/${brand.slug}/${brand.popularModels[0]?.slug ?? ""}`}
                    className="inline-block mt-4"
                  >
                    <Button>Explore a model</Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
